import React from "react";
import ReactModal from "react-modal";
import Loader from "../../../common/loader.jsx";
import axios, {
  errorHandler,
  responseHandler,
} from "../../../requests/axios.js";
import { toast } from "react-toastify";

const PERMISSION_LABELS = {
  one_link: "One Link Bridge",
  hbbtv_viewership: "HbbTV Viewership",
};

export default function ManagePermissionsDialog({ onClose, onConfirm, isOpen, user }) {
  const [allPermissions, setAllPermissions] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen || !user) {
      return;
    }

    setLoading(true);
    Promise.all([
      axios.get("api/permissions"),
      axios.get(`api/users/${user.id}/permissions`),
    ])
      .then(([permsResp, userPermsResp]) => {
        setAllPermissions(permsResp.data);
        setSelectedIds(new Set(userPermsResp.data.map((p) => p.permission_id)));
      })
      .catch(
        errorHandler((e) => {
          console.error(e);
          toast.error("Error loading permissions");
        })
      )
      .finally(() => setLoading(false));
  }, [isOpen, user]);

  function toggle(permissionId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  }

  function save() {
    setSaving(true);
    axios
      .post(`api/users/${user.id}/permissions`, {
        permissions: Array.from(selectedIds),
      })
      .then(
        responseHandler(() => {
          toast.success("Permissions updated");
          if (onConfirm) onConfirm();
          onClose();
        })
      )
      .catch(
        errorHandler((e) => {
          console.error(e);
          toast.error("Error saving permissions");
        })
      )
      .finally(() => setSaving(false));
  }

  return (
    <ReactModal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false}>
      <div className="v-react-modal__inner modal-width">
        <div className="v-react-modal__heading">
          {user ? `Permissions for ${user.name}` : "Permissions"}
        </div>
        <div className="v-react-modal__body modal-body">
          {loading ? (
            <Loader width={45} height={45} />
          ) : allPermissions.length === 0 ? (
            <p>No permissions are configured. Run the database seeder.</p>
          ) : (
            <ul className="permissions-list">
              {allPermissions.map((permission) => (
                <li key={permission.permission_id} className="permissions-list__item">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(permission.permission_id)}
                      onChange={() => toggle(permission.permission_id)}
                    />
                    <span>
                      {PERMISSION_LABELS[permission.name] || permission.name}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="v-react-modal__footer">
          {saving ? (
            <Loader width={45} height={45} />
          ) : (
            <React.Fragment>
              <button className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={save}
                disabled={loading}
              >
                Save
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </ReactModal>
  );
}
